/**
 * TokenCounter — Compact token counter for the public chat toolbar.
 *
 * Shows: "1,234 / 128K" with color coding:
 *   - Default: zinc (under 60%)
 *   - Warning: amber (60-85%)
 *   - Danger: red (85%+)
 *
 * Hover tooltip shows breakdown: system prompt, history, current message.
 * Click opens a small popover with adjustable history depth.
 *
 * @param {object} props
 * @param {string}  props.systemPrompt    — Current system prompt text
 * @param {string}  props.currentMessage  — Current text in the input box
 * @param {Array}   props.messages        — Chat history entries
 * @param {number}  props.contextWindow   — Model's max_input_tokens
 * @param {number}  props.historyDepth    — Current history depth
 * @param {Function} props.onChangeDepth  — (newDepth) => void
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { countContextTokens, formatTokenCount } from "../utils/TokenCounter";

const TokenCounter = ({
  systemPrompt = "",
  currentMessage = "",
  messages = [],
  contextWindow = 128000,
  historyDepth = 5,
  onChangeDepth,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef(null);

  // Close popover on outside click
  useEffect(() => {
    if (!popoverOpen) return;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [popoverOpen]);

  // Count tokens
  const { total, breakdown } = useMemo(
    () =>
      countContextTokens({
        systemPrompt,
        currentMessage,
        history: messages,
        historyDepth,
      }),
    [systemPrompt, currentMessage, messages, historyDepth],
  );

  const ratio = contextWindow > 0 ? total / contextWindow : 0;
  const colorClass =
    ratio >= 0.85
      ? "text-red-500 dark:text-red-400"
      : ratio >= 0.6
        ? "text-amber-500 dark:text-amber-400"
        : "text-zinc-400 dark:text-zinc-500";

  const barColor =
    ratio >= 0.85
      ? "bg-red-500"
      : ratio >= 0.6
        ? "bg-amber-500"
        : "bg-[#0EA5E9]";

  return (
    <div className="relative" ref={popoverRef}>
      {/* Compact display */}
      <button
        onClick={() => setPopoverOpen(!popoverOpen)}
        className={`inline-flex items-center gap-1 py-1 text-[12px] tabular-nums transition-colors hover:text-zinc-600 dark:hover:text-zinc-300 ${colorClass}`}
        title={`${formatTokenCount(total)} / ${formatTokenCount(contextWindow)} tokens`}
      >
        <span>{formatTokenCount(total)}</span>
        <span className="text-zinc-300 dark:text-zinc-700">/</span>
        <span>{formatTokenCount(contextWindow)}</span>
        <ChevronDown className="h-2.5 w-2.5 shrink-0" />
      </button>

      {/* Popover */}
      {popoverOpen && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg z-[80] p-3">
          {/* Progress bar */}
          <div className="h-1.5 rounded-full bg-zinc-100 dark:bg-zinc-800 mb-3 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${barColor}`}
              style={{ width: `${Math.min(100, ratio * 100)}%` }}
            />
          </div>

          {/* Breakdown */}
          <div className="space-y-1.5 text-[11px]">
            <div className="flex justify-between">
              <span className="text-zinc-500 dark:text-zinc-400">
                System prompt
              </span>
              <span className="text-zinc-700 dark:text-zinc-300 tabular-nums">
                {formatTokenCount(breakdown.system)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500 dark:text-zinc-400">
                History ({historyDepth} pairs)
              </span>
              <span className="text-zinc-700 dark:text-zinc-300 tabular-nums">
                {formatTokenCount(breakdown.history)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500 dark:text-zinc-400">
                Current message
              </span>
              <span className="text-zinc-700 dark:text-zinc-300 tabular-nums">
                {formatTokenCount(breakdown.current)}
              </span>
            </div>
            <div className="flex justify-between pt-1.5 border-t border-zinc-100 dark:border-zinc-800">
              <span className="text-zinc-700 dark:text-zinc-200 font-medium">
                Total
              </span>
              <span className="text-zinc-700 dark:text-zinc-200 font-medium tabular-nums">
                {formatTokenCount(total)}
              </span>
            </div>
          </div>

          {/* History depth control */}
          <div className="mt-3 pt-2.5 border-t border-zinc-100 dark:border-zinc-800">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-zinc-500 dark:text-zinc-400">
                History depth
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onChangeDepth?.(Math.max(0, historyDepth - 1))}
                  disabled={historyDepth <= 0}
                  className="w-6 h-6 rounded flex items-center justify-center text-[12px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-30 transition-colors"
                >
                  −
                </button>
                <span className="w-6 text-center text-[12px] font-medium text-zinc-700 dark:text-zinc-300 tabular-nums">
                  {historyDepth}
                </span>
                <button
                  onClick={() =>
                    onChangeDepth?.(Math.min(20, historyDepth + 1))
                  }
                  disabled={historyDepth >= 20}
                  className="w-6 h-6 rounded flex items-center justify-center text-[12px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-30 transition-colors"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenCounter;
